import type { CanvasVariant } from "../canvas/variant-resolver";

/**
 * Mirrors artwork-sizing.ts in the backend. Both must agree on what is
 * sellable: the storefront decides what to show, the API decides what to
 * accept, and a customer who is offered a size the server then refuses has
 * been wasted.
 */
/**
 * The resolution floor, by what the picture is printed on.
 *
 * Canvas has a visible weave and hangs at viewing distance, so labs accept
 * 100-120. Paper hides nothing and 150 is the photo standard. A single flat
 * floor picked badly in both directions: it refused canvas sizes that print
 * perfectly well, and allowed paper prints where the softness shows.
 *
 * MUST match photo-upload-backend/src/core/artwork/artwork-sizing.ts. If these
 * drift, the gallery offers a size the server refuses at checkout and the
 * customer hits a dead end after choosing it.
 */
export const MIN_PRINT_DPI_BY_MATERIAL: Record<string, number> = {
  canvas: 100,
  // 150 is the photo-paper ideal and was tried here, but the catalogue's
  // masters are ~3840 px scans: holding paper to 150 left 23 of 159 artworks
  // with nothing to sell at all, against 1 today, because paper is currently
  // the only material offered. 120 is what the shop has always used and what
  // those masters carry. Revisit if the masters are ever re-ingested larger,
  // or once canvas is back and paper is no longer carrying the catalogue
  // alone.
  paper: 120,
};

/** Canvas is the catalogue's default; an unknown material must not empty the list. */
export const DEFAULT_MIN_PRINT_DPI = MIN_PRINT_DPI_BY_MATERIAL.canvas;

/** The floor for a material, falling back to canvas for anything unrecognised. */
export function dpiFloorFor(material: string | undefined | null): number {
  if (!material) return DEFAULT_MIN_PRINT_DPI;
  return MIN_PRINT_DPI_BY_MATERIAL[material] ?? DEFAULT_MIN_PRINT_DPI;
}

/** Kept for callers with no material to hand. Prefer dpiFloorFor(). */
export const MIN_PRINT_DPI = DEFAULT_MIN_PRINT_DPI;
export const ASPECT_TOLERANCE = 0.08;

const MM_PER_INCH = 25.4;

export interface ArtworkSource {
  widthPx: number;
  heightPx: number;
}

export interface OfferedSize {
  variant: CanvasVariant;
  /** Trim size turned to suit the artwork. */
  widthMm: number;
  heightMm: number;
  dpi: number;
}

/** Orientation-independent proportion, always >= 1. */
export function longRatio(width: number, height: number): number {
  const w = Math.max(width, height);
  const h = Math.min(width, height);
  return h > 0 ? w / h : 1;
}

export function aspectFits(
  source: ArtworkSource,
  printSpec: { widthMm: number; heightMm: number },
  tolerance = ASPECT_TOLERANCE,
): boolean {
  if (!source.widthPx || !source.heightPx) return false;
  if (!printSpec.widthMm || !printSpec.heightMm) return false;
  const want = longRatio(source.widthPx, source.heightPx);
  const have = longRatio(printSpec.widthMm, printSpec.heightMm);
  return Math.abs(have - want) / want <= tolerance;
}

export function printDpi(
  source: ArtworkSource,
  printSpec: { widthMm: number; heightMm: number; bleedMm?: number },
): number {
  const bleed = printSpec.bleedMm ?? 0;
  const sourceLandscape = source.widthPx >= source.heightPx;
  const long = Math.max(printSpec.widthMm, printSpec.heightMm) + 2 * bleed;
  const short = Math.min(printSpec.widthMm, printSpec.heightMm) + 2 * bleed;
  const acrossMm = sourceLandscape ? long : short;
  const downMm = sourceLandscape ? short : long;
  if (!acrossMm || !downMm) return 0;

  return Math.floor(
    Math.min(
      source.widthPx / (acrossMm / MM_PER_INCH),
      source.heightPx / (downMm / MM_PER_INCH),
    ),
  );
}

/**
 * The sizes this piece can honestly be sold at, smallest first.
 *
 * Sizes failing either gate are absent rather than disabled. On the canvas
 * editor a poor size is shown with a warning, because the customer chose the
 * photo and may accept the trade. Here the shop chose the master, so offering
 * something it cannot print is a shop error and there is nothing for the
 * customer to weigh up.
 */
export function offeredSizes(
  source: ArtworkSource,
  variants: CanvasVariant[],
  options: { minDpi?: number; tolerance?: number } = {},
): OfferedSize[] {
  // An explicit minDpi overrides everything; otherwise each variant is judged
  // by its own material.
  const sourceLandscape = source.widthPx >= source.heightPx;

  const offered: OfferedSize[] = [];
  for (const variant of variants ?? []) {
    if (variant.active === false) continue;
    const spec = variant.printSpec;
    if (!spec) continue;
    if (!aspectFits(source, spec, options.tolerance)) continue;

    const dpi = printDpi(source, spec);
    const material =
      (variant as { material?: string | null; axisValues?: Record<string, string> })
        .material ??
      (variant as { axisValues?: Record<string, string> }).axisValues?.material;
    const floor = options.minDpi ?? dpiFloorFor(material);
    if (dpi < floor) continue;

    const long = Math.max(spec.widthMm, spec.heightMm);
    const short = Math.min(spec.widthMm, spec.heightMm);
    offered.push({
      variant,
      widthMm: sourceLandscape ? long : short,
      heightMm: sourceLandscape ? short : long,
      dpi,
    });
  }

  return offered.sort(
    (a, b) => a.widthMm * a.heightMm - b.widthMm * b.heightMm,
  );
}

/**
 * A variant's price in one currency.
 *
 * Mirrors resolvePriceForCurrency in the backend, including its
 * case-insensitive key match — the data holds both "pln" and "PLN". The
 * storefront has to agree with the server here: showing the euro number under
 * a złoty sign is worse than either being wrong on its own, because the
 * customer is quoted one price and charged another.
 */
export function variantPrice(
  variant: { price: number; priceCurrencies?: Record<string, number> | null },
  currency: string | undefined,
): number {
  const cur = (currency || "eur").toLowerCase();
  if (cur === "eur" || !variant.priceCurrencies) return variant.price;
  for (const [key, value] of Object.entries(variant.priceCurrencies)) {
    if (key.toLowerCase() === cur && value != null) return Number(value);
  }
  return variant.price;
}
